import { SESSION_RECORDING_EVENTS, sendSubscriptionFunnelEvent } from "utils/session-recording"

const READING_DWELL_MS = 3000
const ENGAGEMENT_INTERACTION_TYPES = ["pointerdown", "keydown", "change"]

// Tracks the two engagement signals inside the signup popup: keeping it
// visible long enough to suggest reading, and the first real interaction
// with its controls. Both are alternatives for the same funnel step.
// Returns a function that stops the tracking.
export default function startEngagementTracking(dialog: HTMLElement): () => void {
  let readingTracked = false
  let interactionTracked = false
  let remainingMs = READING_DWELL_MS
  let resumedAt = 0
  let timerId: number | null = null

  const resumeReadingTimer = () => {
    if (readingTracked || timerId !== null) return
    if (document.visibilityState !== "visible") return

    resumedAt = Date.now()
    timerId = window.setTimeout(() => {
      timerId = null
      readingTracked = true
      sendSubscriptionFunnelEvent(SESSION_RECORDING_EVENTS.subPopupReading)
    }, remainingMs)
  }

  const pauseReadingTimer = () => {
    if (timerId === null) return

    window.clearTimeout(timerId)
    timerId = null
    remainingMs = Math.max(remainingMs - (Date.now() - resumedAt), 0)
  }

  const interactionListener = (event: Event) => {
    if (interactionTracked) return
    // Closing the popup is not engagement
    if ((event as KeyboardEvent).key === "Escape") return
    if (event.target === dialog) return
    if (
      event.target instanceof Element &&
      event.target.closest("[data-session-recording-ignore]")
    ) {
      return
    }

    interactionTracked = true
    sendSubscriptionFunnelEvent(SESSION_RECORDING_EVENTS.subPopupInteraction)
  }

  const visibilityListener = () => {
    if (document.visibilityState === "visible") {
      resumeReadingTimer()
    } else {
      pauseReadingTimer()
    }
  }

  ENGAGEMENT_INTERACTION_TYPES.forEach((type) => {
    dialog.addEventListener(type, interactionListener)
  })
  document.addEventListener("visibilitychange", visibilityListener)
  resumeReadingTimer()

  return () => {
    pauseReadingTimer()
    document.removeEventListener("visibilitychange", visibilityListener)
    ENGAGEMENT_INTERACTION_TYPES.forEach((type) => {
      dialog.removeEventListener(type, interactionListener)
    })
  }
}
